import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, Send, Copy, Edit, Clock, Mail, FileText,
  User, MapPin, Phone, Building2, Zap, Upload, X, Paperclip,
  ExternalLink, MessageSquare, Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  STATUS_OPTIONS, SUBJECT_FIELDS, getStatusColor, getStatusLabel, getSubjectLabel
} from '../lib/subjectFields';
import { useAuth } from '../contexts/AuthContext';

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [emails, setEmails] = useState([]);
  const [history, setHistory] = useState([]);
  const [partners, setPartners] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);

  const loadData = () => {
    Promise.all([
      API.get(`/leads/${id}`),
      API.get(`/emails/lead/${id}`),
      API.get(`/leads/${id}/history`),
      API.get('/partners', { params: { active_only: true } }),
      API.get(`/leads/${id}/comments`)
    ]).then(([l, e, h, p, c]) => {
      setLead(l.data);
      setEmails(e.data);
      setHistory(h.data);
      setPartners(p.data);
      setComments(c.data);
      setLoading(false);
    }).catch(() => {
      toast.error('Lead non trouve');
      navigate('/leads');
    });
  };

  useEffect(() => { loadData(); }, [id]);

  const handleCommentFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCommentAttachments(prev => [...prev, res.data]);
      } catch { toast.error(`Erreur upload: ${file.name}`); }
    }
    e.target.value = '';
  };

  const submitComment = async () => {
    if (!newComment.trim() && commentAttachments.length === 0) return;
    setSubmittingComment(true);
    try {
      await API.post(`/leads/${id}/comments`, { text: newComment, attachments: commentAttachments });
      setNewComment('');
      setCommentAttachments([]);
      const [c, h] = await Promise.all([API.get(`/leads/${id}/comments`), API.get(`/leads/${id}/history`)]);
      setComments(c.data);
      setHistory(h.data);
      toast.success('Commentaire ajoute');
    } catch { toast.error('Erreur'); }
    finally { setSubmittingComment(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await API.patch(`/leads/${id}/status`, { status: newStatus });
      setLead(prev => ({ ...prev, status: newStatus }));
      const h = await API.get(`/leads/${id}/history`);
      setHistory(h.data);
      toast.success('Statut mis a jour');
    } catch { toast.error('Erreur'); }
  };

  const handleDuplicate = async () => {
    try {
      const res = await API.post(`/leads/${id}/duplicate`);
      toast.success('Lead duplique');
      navigate(`/leads/${res.data.id}`);
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/leads/${id}`);
      toast.success('Lead supprime');
      navigate('/leads');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const gen = await API.post('/emails/generate', { lead_id: id });
      setEmailPreview(gen.data);
      const res = await API.post('/emails/send', {
        lead_id: id, partner_ids: selectedPartners,
        subject: gen.data.subject, body: gen.data.body
      });
      setEmails(prev => [...res.data.results, ...prev]);
      setLead(prev => ({ ...prev, status: 'envoye_partenaire' }));
      const h = await API.get(`/leads/${id}/history`);
      setHistory(h.data);
      setSendOpen(false);
      setSelectedPartners([]);
      toast.success('Email(s) envoye(s)');
    } catch { toast.error('Erreur d\'envoi'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent animate-spin" style={{borderRadius:'50%'}} /></div>;
  if (!lead) return null;

  const subjectConfig = SUBJECT_FIELDS[lead.subject];
  const filteredPartners = partners.filter(p => !lead.subject || p.categories.includes(lead.subject));

  const InfoRow = ({ icon: Icon, label, value }) => (
    value ? (
      <div className="flex items-start gap-2 py-1.5">
        <Icon size={14} className="text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
        <div>
          <p className="text-[11px] text-muted-foreground">{label}</p>
          <p className="text-sm">{value}</p>
        </div>
      </div>
    ) : null
  );

  return (
    <div className="space-y-4 fade-in" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-to-leads">
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-xl font-heading font-bold">{lead.client_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px]">{getSubjectLabel(lead.subject)}</Badge>
              <Badge className="text-[10px]" style={{ backgroundColor: getStatusColor(lead.status), color: '#fff' }}>
                {getStatusLabel(lead.status)}
              </Badge>
              <span className="text-xs text-muted-foreground">{lead.city}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={lead.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="status-change-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-lead-btn">
            <Copy size={14} className="mr-1" /> Dupliquer
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/leads/${id}/edit`)} data-testid="edit-lead-btn">
            <Edit size={14} className="mr-1" /> Modifier
          </Button>
          <Dialog open={sendOpen} onOpenChange={setSendOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white" size="sm" data-testid="open-send-dialog">
                <Send size={14} className="mr-1" /> Envoyer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Envoyer a un partenaire</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {filteredPartners.map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-2 border hover:bg-slate-50 cursor-pointer"
                    data-testid={`send-partner-${p.id}`}>
                    <Checkbox checked={selectedPartners.includes(p.id)}
                      onCheckedChange={() => setSelectedPartners(prev =>
                        prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                      )} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email_primary}</p>
                    </div>
                    {p.form_url && (
                      <div className="flex flex-col items-end shrink-0">
                        <a href={p.form_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#397DFF] hover:underline font-medium"
                          onClick={e => e.stopPropagation()}
                          data-testid={`detail-partner-form-${p.id}`}>
                          <ExternalLink size={12} /> {p.name === 'Beev' ? 'Formulaire Beev' : 'Formulaire'}
                        </a>
                        {p.name === 'Beev' && (
                          <span className="text-[10px] text-orange-600 font-medium mt-0.5">
                            Formulaire obligatoire en plus de l'email
                          </span>
                        )}
                      </div>
                    )}
                  </label>
                ))}
                {filteredPartners.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucun partenaire pour ce sujet</p>
                )}
                <Button className="w-full bg-[#397DFF] hover:bg-[#2563EB] text-white"
                  onClick={handleSendEmail} disabled={sending || selectedPartners.length === 0}
                  data-testid="confirm-send-btn">
                  <Send size={14} className="mr-1" /> {sending ? 'Envoi...' : `Envoyer a ${selectedPartners.length} partenaire(s)`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {user?.role === 'equipe_interne' && (
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-lead-btn">
                  <Trash2 size={14} className="mr-1" /> Supprimer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-red-600">Supprimer ce lead ?</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Etes-vous sur de vouloir supprimer le lead <strong>{lead.client_name}</strong> ?
                    Cette action est irreversible et supprimera egalement l'historique, les commentaires et les emails associes.
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="cancel-delete-btn">
                      Annuler
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                      <Trash2 size={14} className="mr-1" /> {deleting ? 'Suppression...' : 'Supprimer'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info">
        <TabsList className="bg-slate-100" data-testid="lead-tabs">
          <TabsTrigger value="info" data-testid="tab-info">Informations</TabsTrigger>
          <TabsTrigger value="suivi" data-testid="tab-suivi">Suivi ({comments.length})</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails ({emails.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Informations client</CardTitle></CardHeader>
              <CardContent className="space-y-0">
                <InfoRow icon={Building2} label="Societe" value={lead.client_name} />
                <InfoRow icon={User} label="Interlocuteur" value={[lead.contact_name, lead.contact_function].filter(Boolean).join(' - ')} />
                <InfoRow icon={Phone} label="Telephone" value={lead.phone} />
                <InfoRow icon={Mail} label="Email" value={lead.email} />
                <InfoRow icon={MapPin} label="Adresse" value={[lead.address, lead.postal_code, lead.city].filter(Boolean).join(', ')} />
                <InfoRow icon={FileText} label="SIREN" value={lead.siren} />
                <InfoRow icon={Zap} label="Type" value={lead.client_type} />
              </CardContent>
            </Card>

            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Origine & Classification</CardTitle></CardHeader>
              <CardContent className="space-y-0">
                <InfoRow icon={User} label="Origine de la demande" value={lead.lead_source || lead.origin || '-'} />
                <InfoRow icon={Mail} label="Email collaborateur PDE" value={lead.origin} />
                <InfoRow icon={Zap} label="Sujet" value={getSubjectLabel(lead.subject)} />
                <InfoRow icon={Clock} label="Maturite" value={lead.maturity} />
                <InfoRow icon={FileText} label="Cree par" value={lead.created_by_name} />
                <InfoRow icon={Clock} label="Date creation" value={lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-FR') : '-'} />
              </CardContent>
            </Card>

            {subjectConfig && (
              <Card className="border shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Details - {subjectConfig.label}</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {subjectConfig.fields.map(f => {
                    const val = lead.specific_fields?.[f.name];
                    return val ? (
                      <div key={f.name} className="flex justify-between py-1 border-b border-dashed border-slate-100 last:border-0">
                        <span className="text-xs text-muted-foreground">{f.label}</span>
                        <span className="text-sm font-medium text-right">{val}</span>
                      </div>
                    ) : null;
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {lead.comments && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Commentaires initiaux</CardTitle></CardHeader>
              <CardContent><p className="text-sm whitespace-pre-wrap">{lead.comments}</p></CardContent>
            </Card>
          )}

          {lead.attachments && lead.attachments.length > 0 && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pieces jointes du lead</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {lead.attachments.map((att, idx) => (
                  <a key={idx} href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-slate-50 border text-sm hover:bg-slate-100 transition-colors">
                    <Paperclip size={13} className="text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate text-[#397DFF]">{att.original_name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{((att.size || 0) / 1024).toFixed(0)} Ko</span>
                  </a>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SUIVI TAB */}
        <TabsContent value="suivi" className="space-y-4">
          {/* Add comment form */}
          <Card className="border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">Ajouter un commentaire / une piece jointe</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire de suivi (devis recu, reponse partenaire, echange client...)"
                className="text-sm" rows={3} data-testid="suivi-comment-input" />
              {commentAttachments.length > 0 && (
                <div className="space-y-1">
                  {commentAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 border text-sm">
                      <Paperclip size={13} className="text-muted-foreground" />
                      <span className="flex-1 truncate">{att.original_name}</span>
                      <span className="text-[10px] text-muted-foreground">{((att.size || 0) / 1024).toFixed(0)} Ko</span>
                      <button onClick={() => setCommentAttachments(prev => prev.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1 cursor-pointer text-sm text-[#397DFF] hover:text-[#2563EB] font-medium" data-testid="suivi-add-file-btn">
                  <input type="file" multiple className="hidden" onChange={handleCommentFileUpload} />
                  <Upload size={14} /> + Piece jointe
                </label>
                <div className="flex-1" />
                <Button size="sm" className="bg-[#397DFF] hover:bg-[#2563EB] text-white"
                  onClick={submitComment} disabled={submittingComment || (!newComment.trim() && commentAttachments.length === 0)}
                  data-testid="suivi-submit-btn">
                  <MessageSquare size={14} className="mr-1" /> {submittingComment ? 'Envoi...' : 'Ajouter'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Comments list */}
          {comments.length === 0 ? (
            <Card className="border"><CardContent className="py-8 text-center text-sm text-muted-foreground">Aucun commentaire de suivi</CardContent></Card>
          ) : comments.map(c => (
            <Card key={c.id} className="border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{c.user_name}</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleString('fr-FR')}</span>
                </div>
                {c.text && <p className="text-sm whitespace-pre-wrap mb-2">{c.text}</p>}
                {c.attachments && c.attachments.length > 0 && (
                  <div className="space-y-1">
                    {c.attachments.map((att, idx) => (
                      <a key={idx} href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-1.5 bg-slate-50 border text-xs hover:bg-slate-100 transition-colors">
                        <Paperclip size={12} className="text-muted-foreground" />
                        <span className="text-[#397DFF] truncate">{att.original_name}</span>
                        <span className="text-muted-foreground">{((att.size || 0) / 1024).toFixed(0)} Ko</span>
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="emails" className="space-y-3">
          {emails.length === 0 ? (
            <Card className="border"><CardContent className="py-8 text-center text-sm text-muted-foreground">Aucun email envoye</CardContent></Card>
          ) : emails.map(em => (
            <Card key={em.id} className="border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{em.partner_name}</span>
                    <span className="text-xs text-muted-foreground">({em.to_email})</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]"
                    style={{ borderColor: em.status === 'sent' ? '#10B981' : em.status === 'simulated' ? '#F59E0B' : '#EF4444',
                             color: em.status === 'sent' ? '#10B981' : em.status === 'simulated' ? '#F59E0B' : '#EF4444' }}>
                    {em.status === 'sent' ? 'Envoye' : em.status === 'simulated' ? 'Simule' : 'Echec'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-1">{em.subject}</p>
                <p className="text-[11px] text-muted-foreground">
                  Par {em.sent_by_name} le {new Date(em.sent_at).toLocaleString('fr-FR')}
                </p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="history" className="space-y-0">
          <Card className="border shadow-sm">
            <CardContent className="p-0">
              <div className="divide-y">
                {history.map(h => (
                  <div key={h.id} className="px-4 py-3 flex items-start gap-3">
                    <div className="w-2 h-2 mt-1.5 bg-[#397DFF] shrink-0" style={{borderRadius:'50%'}} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{h.details}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {h.user_name} - {new Date(h.timestamp).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
                {history.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">Aucun historique</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
